import Swiper from 'swiper';
import { Pagination, FreeMode, Thumbs, Zoom } from 'swiper/modules';
import { setupProductModels } from './product-model';

const refreshActiveModelViewer = (swiper) => {
  const activeSlide = swiper.slides?.[swiper.activeIndex];
  const modelViewer = activeSlide?.querySelector('model-viewer');
  if (!modelViewer) return;

  setupProductModels();
  modelViewer.dispatchEvent(new Event('resize'));
};

const initProductGallery = (gallery) => {
  const mainEl = gallery.querySelector('[data-product-gallery-main]');
  if (!mainEl) return;

  const thumbsEl = gallery.querySelector('[data-product-gallery-thumbs]');
  const paginationEl = gallery.querySelector('[data-product-gallery-pagination]');
  const thumbsPrev = gallery.querySelector('[data-product-gallery-thumbs-prev]');
  const thumbsNext = gallery.querySelector('[data-product-gallery-thumbs-next]');

  if (mainEl.swiper) {
    mainEl.swiper.destroy(true, true);
  }
  if (thumbsEl?.swiper) {
    thumbsEl.swiper.destroy(true, true);
  }

  let thumbsSwiper = null;

  if (thumbsEl) {
    thumbsSwiper = new Swiper(thumbsEl, {
      modules: [FreeMode],
      spaceBetween: 15,
      slidesPerView: 4.5,
      freeMode: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      breakpoints: {
        1024: {
          slidesPerView: 'auto',
        },
      },
    });
  }

  const mainConfig = {
    modules: [Pagination, Zoom, ...(thumbsSwiper ? [Thumbs] : [])],
    slidesPerView: 1,
    zoom: true,
    ...(thumbsSwiper ? { thumbs: { swiper: thumbsSwiper } } : {}),
  };

  if (paginationEl) {
    mainConfig.pagination = {
      el: paginationEl,
      clickable: true,
    };
  }

  const mainSwiper = new Swiper(mainEl, mainConfig);

  const wireNavToMain = (button, direction) => {
    if (!button) return;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      if (direction === 'prev') {
        mainSwiper.slidePrev();
      } else {
        mainSwiper.slideNext();
      }
    });
  };

  wireNavToMain(thumbsPrev, 'prev');
  wireNavToMain(thumbsNext, 'next');

  const thumbButtons = gallery.querySelectorAll('[data-product-gallery-thumb]');

  const setActiveThumb = (index) => {
    thumbButtons.forEach((btn, i) => {
      const isActive = i === index;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  };

  const syncNavState = () => {
    const atStart = mainSwiper.isBeginning;
    const atEnd = mainSwiper.isEnd;
    [thumbsPrev, thumbsNext].forEach((button) => {
      if (!button) return;
      button.disabled = button === thumbsPrev ? atStart : atEnd;
    });
  };

  if (thumbButtons.length) {
    thumbButtons.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const index = Number(btn.dataset.mediaIndex);
        if (!Number.isFinite(index)) return;
        mainSwiper.slideTo(index);
      });
    });

    mainSwiper.on('slideChange', () => {
      setActiveThumb(mainSwiper.activeIndex);
    });

    setActiveThumb(mainSwiper.activeIndex);
  }

  mainSwiper.on('slideChange', syncNavState);
  mainSwiper.on('slideChange', () => refreshActiveModelViewer(mainSwiper));
  syncNavState();
  refreshActiveModelViewer(mainSwiper);

  mainEl.classList.add('swiper--active');
  if (thumbsEl) {
    thumbsEl.classList.add('swiper--active');
  }

  gallery._productGalleryMainSwiper = mainSwiper;
  gallery._productGalleryThumbsSwiper = thumbsSwiper;
};

const initProductGalleries = (root = document) => {
  root.querySelectorAll('[data-product-gallery]').forEach(initProductGallery);
};

export const getProductGalleryMainSwiper = () =>
  document.querySelector('[data-product-gallery]')?._productGalleryMainSwiper ?? null;

export default initProductGalleries;
